import {useEffect} from 'react'
import {MAX_QUERIES_TO_MARK_FOR_PREHEATING, useRelayCachePreheatContext} from '../contexts/RelayCachePreheatContext'
import type {ConcreteRequest, GraphQLTaggedNode} from 'relay-runtime'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isLoggedIn} from '@github-ui/client-env'

type UseCachePreheatQueryProps = {
  query?: GraphQLTaggedNode
  variables: Record<string, unknown>
  source: string
  enabled?: boolean
  excludeFromCacheKey?: string[]
  highPriority?: boolean
}

type useCachePreheatQueriesProps = {
  queries: UseCachePreheatQueryProps[]
}

type UseCachePreheatTaskProps = {
  key: string
  task: () => Promise<unknown>
  enabled?: boolean
  highPriority?: boolean
}

type UseCachePreheatTasksProps = {
  tasks: UseCachePreheatTaskProps[]
}

export function useCachePreheatQuery({
  query,
  variables,
  source,
  enabled = true,
  excludeFromCacheKey,
  highPriority,
}: UseCachePreheatQueryProps) {
  const {markQueryForPreheating} = useRelayCachePreheatContext()

  const preHeatingEnabled = !isFeatureEnabled('disable_preheating_for_issues') && isLoggedIn()

  useEffect(() => {
    if (!enabled || !query || !preHeatingEnabled) {
      return
    }
    markQueryForPreheating(query as ConcreteRequest, variables, source, excludeFromCacheKey, highPriority)
  }, [markQueryForPreheating, variables, query, source, enabled, excludeFromCacheKey, highPriority, preHeatingEnabled])
}

export function useCachePreheatQueries({queries}: useCachePreheatQueriesProps) {
  const {markQueryForPreheating} = useRelayCachePreheatContext()

  const preHeatingEnabled = !isFeatureEnabled('disable_preheating_for_issues') && isLoggedIn()

  useEffect(() => {
    for (const query of queries) {
      const enabled = query.enabled ?? true
      if (!enabled || !query.query || !preHeatingEnabled) {
        continue
      }
      markQueryForPreheating(
        query.query as ConcreteRequest,
        query.variables,
        query.source,
        query.excludeFromCacheKey,
        query.highPriority,
      )
    }
  }, [markQueryForPreheating, queries, preHeatingEnabled])
}

export function useCachePreheatTask({key, task, enabled = true, highPriority}: UseCachePreheatTaskProps) {
  const {markTaskForPreheating} = useRelayCachePreheatContext()
  const preHeatingEnabled = !isFeatureEnabled('disable_preheating_for_issues') && isLoggedIn()

  useEffect(() => {
    if (!enabled || !preHeatingEnabled) return
    markTaskForPreheating(key, task, highPriority)
  }, [enabled, highPriority, key, markTaskForPreheating, preHeatingEnabled, task])
}

export function useCachePreheatTasks({tasks}: UseCachePreheatTasksProps) {
  const {markTaskForPreheating} = useRelayCachePreheatContext()
  const preHeatingEnabled = !isFeatureEnabled('disable_preheating_for_issues') && isLoggedIn()

  useEffect(() => {
    if (!preHeatingEnabled) return
    const enabledTasks = tasks.filter(task => task.enabled !== false).slice(0, MAX_QUERIES_TO_MARK_FOR_PREHEATING)
    for (const task of enabledTasks) {
      markTaskForPreheating(task.key, task.task, task.highPriority)
    }
  }, [markTaskForPreheating, preHeatingEnabled, tasks])
}
